import React from "react"

import {
  FloorTypename,
  ProductPositionTypename,
  RoomTypename
} from "shared/types/Project"
import { DragSource, isDragSource } from "shared/types/drag-and-drop/DragSource"
import { DragGhost } from "shared/ui/drag-ghost/DragGhost"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import { getProductType } from "entities/products/lib/getProductType"
import { useProductObjCache } from "entities/products/model/store/useProductObjCache"
import { formatRoomTitle } from "entities/rooms/ui/room-title/RoomTitle"
import z from "zod"

interface ProductPositionTableDragGhostProps {
  error?: boolean
  dragSource: DragSource
}

export const ProjectPageDragGhost = React.memo(
  React.forwardRef<HTMLDivElement, ProductPositionTableDragGhostProps>(
    ({ dragSource, error }, ref) => {
      const { getCacheForVariantKey } = useProductObjCache()
      if (
        isDragSource(dragSource, {
          dataSchema: z.object({
            id: z.string(),
            number: z.string().optional().nullish(),
            label: z.string().optional().nullish(),
            __typename: z.literal(RoomTypename)
          })
        })
      ) {
        return (
          <DragGhost
            ref={ref}
            icon={IconName.ROOM}
            text={formatRoomTitle(
              dragSource.data.number,
              dragSource.data.label
            )}
            error={error}
          />
        )
      }

      if (
        isDragSource(dragSource, {
          dataSchema: z.object({
            id: z.string(),
            variantKey: z.string(),
            __typename: z.literal(ProductPositionTypename)
          })
        })
      ) {
        return (
          <DragGhost
            ref={ref}
            icon={IconName.PRODUCTS}
            text={
              getCacheForVariantKey(dragSource.data.variantKey)?.title ||
              getProductType(dragSource.data.variantKey)
            }
            error={error}
          />
        )
      }
      if (
        isDragSource(dragSource, {
          dataSchema: z.object({
            id: z.string(),
            label: z.string(),
            __typename: z.literal(FloorTypename)
          })
        })
      ) {
        return (
          <DragGhost
            ref={ref}
            icon={IconName.FLOOR}
            text={dragSource.data.label}
            error={error}
          />
        )
      }

      return <DragGhost ref={ref} text={dragSource.label ?? "Unknown"} />
    }
  )
)

ProjectPageDragGhost.displayName = "ProductPositionTableDragGhost"
